import formatter from './formatter';
import { sortValues } from './sort';
import get from 'lodash/get';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import GetDataPointOptions from '../models/utils/GetDataPointOptions';
import { translate } from '../Components/Translate';
import {
    CollectionFilter,
    CollectionFilterInputs,
    CollectionFilterInputType,
} from '../models/configuration/CollectionFilter';
import { ConditionalHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { IConditional } from '@kurtosys/ksys-app-template/dist/models/common';
import { deepMergeObjects } from '@kurtosys/ksys-app-template/dist/utils/object';

export const getDataPoint = (options: GetDataPointOptions) => {
    const {
        dataConfig,
        shareClass,
        locale,
        fundCurrency,
        defaultNoDataPlaceholder,
        dataStore,
        hasPerformance = true,
    } = options;
    let { find: findObj } = dataConfig;
    const {
        property,
        code,
        ccy,
        classification,
        periodicity,
        key,
        index,
        startIndex = 0,
        endIndex,
        format,
        sort,
        noDataPlaceholder = defaultNoDataPlaceholder,
        performanceCheck,
        valuesFilters,
    } = dataConfig;

    if (performanceCheck && !hasPerformance) {
        return noDataPlaceholder;
    }

    // Translate find object values where possible
    if (findObj) {
        const findKeys = Object.keys(findObj);
        if (findKeys) {
            findObj = findKeys.reduce((obj, findKey) => {
                /**
                 * Due to FundPress limitations
                 * properties pub fields are translatable
                 * AND
                 * ONLY the label field for allocations, statistics & timeseries are translatable.
                 */
                if (property === 'properties_pub' || findKey === 'label') {
                    obj[findKey] = translate(
                        dataStore.stores.translation,
                        findObj[findKey]
                    );
                } else {
                    obj[findKey] = findObj[findKey];
                }
                return obj;
            }, {} as any);
        }
    }

    if (property === 'properties_pub') {
        const value = get(
            shareClass,
            `${property}.${code}.${key || 'value'}`,
            noDataPlaceholder
        );

        return format
            ? formatter({
                  dataStore,
                  locale,
                  value,
                  format,
                  type: format.type,
                  currency: fundCurrency,
                  options: format.options,
              })
            : value;
    }

    if (property) {
        const dataSet = get(shareClass, property, []);

        // We should end up with one data object, so get the first item in array
        const data = find(dataSet, {
            ...(code && { code }),
            ...(ccy && { ccy }),
            ...(classification && { classification }),
            ...(periodicity && { periodicity }),
        });

        // Get the values from data
        let values: Array<any> = (data || {}).values || [];

        // Slice values to convert from observable array for compatibility with re-charts & sorting
        values = values.slice();

        // Apply a filter(s) to the values if it is defined
        if (valuesFilters && valuesFilters.length > 0) {
            values = applyCollectionFilters(values, valuesFilters, options);
        }

        // sort
        values = sortValues(values, sort);

        // Slice values using start/end index
        const offsetStartIndex = values.length + endIndex;

        const isTrailing = typeof endIndex === 'number' && endIndex < 0;
        values = isTrailing
            ? values.slice(Math.max(0, offsetStartIndex))
            : values.slice(startIndex, endIndex);

        // If we have an index but no key, return values object at index
        if (isNumber(index) && !key) {
            return index >= 0
                ? get(values, `[${index}]`, {})
                : get(values, `[${values.length - 1}]`, {});
        }

        // If we have a find object but no key, return found values object
        if (findObj && !key) {
            return find(values, findObj) || {};
        }

        // If we have a find object and key, return value of key in found object
        if (findObj && key) {
            const valuesObj = find(values, findObj);
            const value = get(valuesObj, key, noDataPlaceholder);
            return format
                ? formatter({
                      dataStore,
                      locale,
                      value,
                      format,
                      type: format.type,
                      currency: fundCurrency,
                      options: format.options,
                  })
                : value;
        }

        // If we have an index and key, return value at values[index].key
        if (isNumber(index) && key) {
            const value =
                index >= 0
                    ? get(values, `[${index}].${key}`, noDataPlaceholder)
                    : get(
                          values,
                          `[${values.length + index}].${key}`,
                          noDataPlaceholder
                      );

            return format
                ? formatter({
                      dataStore,
                      locale,
                      value,
                      format,
                      type: format.type,
                      currency: fundCurrency,
                      options: format.options,
                  })
                : value;
        }

        return values;
    }

    return noDataPlaceholder;
};

function applyCollectionFilters(
    collection: Array<object>,
    filters: Array<CollectionFilter>,
    options: GetDataPointOptions
) {
    let response: Array<object> = collection;
    if (filters && filters.length > 0) {
        for (const filter of filters) {
            response = applyCollectionFilter(response, filter, options);
        }
    }
    return response;
}

function applyCollectionFilter(
    collection: Array<object>,
    filter: CollectionFilter,
    options: GetDataPointOptions
) {
    let response: Array<object> = collection;
    if (filter) {
        const { conditional, globalCollectionFilterId, inputs } = filter;
        if (conditional) {
            const loadedConditional = loadConditionalInputs(
                conditional,
                inputs,
                options
            );
            const conditionalHelper = new ConditionalHelper(loadedConditional);
            response = collection.filter(item => {
                return conditionalHelper.matches(item);
            });
        } else if (globalCollectionFilterId) {
            const { dataStore } = options;
            if (
                dataStore &&
                dataStore.config &&
                dataStore.config.globalConfig &&
                dataStore.config.globalConfig.collectionFilters &&
                dataStore.config.globalConfig.collectionFilters[
                    globalCollectionFilterId
                ]
            ) {
                // Strip the globalCollectionFilterId from the global filter to stop an infinite loop occurring.
                const {
                    globalCollectionFilterId: recursiveFilterId,
                    ...globalFilter
                } = dataStore.config.globalConfig.collectionFilters[
                    globalCollectionFilterId
                ];
                response = applyCollectionFilter(
                    collection,
                    globalFilter,
                    options
                );
            }
        }
    }

    return response;
}

function loadConditionalInputs(
    conditional: IConditional,
    inputs: CollectionFilterInputs,
    options: GetDataPointOptions
) {
    if (conditional) {
        const clone: IConditional = deepMergeObjects({}, conditional);
        if (inputs) {
            const { conditions } = clone;
            const inputKeys = Object.keys(inputs);
            for (const inputKey of inputKeys) {
                const input = inputs[inputKey];
                const { type, dataType = 'string', dataKey } = input;
                let value = '';
                if (type === 'filter') {
                    const filterSearch = 'filterSearch';
                    value =
                        (options.dataStore &&
                            options.dataStore.stores &&
                            options.dataStore.stores.filter &&
                            options.dataStore.stores.filter.filterValue(
                                dataKey,
                                filterSearch
                            )) ||
                        '';
                }
                for (const condition of conditions) {
                    condition.value = replaceKey(
                        condition.value,
                        'input',
                        inputKey,
                        value,
                        dataType
                    );
                }
            }
        }
        return clone;
    }
}

function replaceKey(
    container: string,
    prefix: string,
    key: string,
    value: any,
    type: CollectionFilterInputType = 'string'
) {
    let response: any = container;
    if (response) {
        const placeholder = `{${prefix}:${key}}`;
        if (response === placeholder) {
            switch (type) {
                case 'boolean':
                    response = parseBoolean(value);
                    break;
                case 'number':
                    response =
                        value !== undefined &&
                        value !== null &&
                        !Number.isNaN(parseFloat(value))
                            ? parseFloat(value)
                            : 0;
                    break;
                case 'string':
                    response =
                        value !== undefined && value !== null
                            ? value.toString()
                            : '';
                    break;
            }
        } else {
            response = response.split(placeholder).join(value);
        }
    }
    return response;
}

function parseBoolean(value: string): boolean {
    switch (value.toString().toLowerCase()) {
        case 'true':
        case 't':
        case 'on':
        case 'yes':
        case 'y':
        case '1':
            return true;
        default:
            return false;
    }
}

export default getDataPoint;



// WEBPACK FOOTER //
// ./src/utils/getDataPoint.ts